/**
 * NotificationDropdown component - displays notification list with actions
 */

import { useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import {
  Bell,
  CheckCheck,
  Archive,
  Trash2,
  MessageSquare,
  UserPlus,
  AlertCircle,
  CheckCircle,
  Calendar,
  Flag,
  X,
} from 'lucide-react';
import { formatDistanceToNow } from '@/i18n/dateFns';
import Avatar from '@components/ui/Avatar';
import { useCommentStore } from '@stores/commentStore';
import type { Notification } from '@services/api';

interface NotificationDropdownProps {
  isOpen: boolean;
  onClose: () => void;
}

// Map notification types to icons
function getNotificationIcon(type: string) {
  switch (type) {
    case 'mention':
    case 'comment':
    case 'reply':
      return MessageSquare;
    case 'task_assigned':
    case 'member_added':
      return UserPlus;
    case 'task_completed':
      return CheckCircle;
    case 'task_due_soon':
    case 'task_overdue':
      return Calendar;
    case 'high_priority':
      return Flag;
    case 'system':
    default:
      return AlertCircle;
  }
}

// Map priority to colors
function getPriorityColor(priority: string) {
  switch (priority) {
    case 'urgent':
      return 'text-red-500';
    case 'high':
      return 'text-orange-500';
    case 'normal':
      return 'text-blue-500';
    case 'low':
    default:
      return 'text-neutral-400';
  }
}

export default function NotificationDropdown({ isOpen, onClose }: NotificationDropdownProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dropdownRef = useRef<HTMLDivElement>(null);

  const {
    notifications,
    notificationsLoading,
    notificationsError,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    archiveNotification,
    archiveAllNotifications,
    clickNotification,
  } = useCommentStore();

  // Fetch notifications when dropdown opens
  useEffect(() => {
    if (isOpen) {
      void fetchNotifications();
    }
  }, [isOpen, fetchNotifications]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  const handleNotificationClick = useCallback(
    async (notification: Notification) => {
      const actionUrl = await clickNotification(notification.id);
      if (actionUrl) {
        navigate(actionUrl);
      }
      onClose();
    },
    [clickNotification, navigate, onClose]
  );

  const handleMarkAllRead = useCallback(async () => {
    await markAllAsRead();
  }, [markAllAsRead]);

  const handleArchiveAll = useCallback(async () => {
    await archiveAllNotifications();
  }, [archiveAllNotifications]);

  if (!isOpen) return null;

  const unreadNotifications = notifications.filter((n) => !n.isRead);
  const readNotifications = notifications.filter((n) => n.isRead);

  return (
    <div
      ref={dropdownRef}
      className={clsx(
        'absolute right-0 top-full z-50 mt-2 w-96 max-w-[calc(100vw-1rem)] rounded-lg shadow-xl',
        'bg-white dark:bg-neutral-900',
        'border border-neutral-200 dark:border-neutral-700',
        'animate-in fade-in slide-in-from-top-2 duration-200'
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between border-b border-neutral-200 p-4 dark:border-neutral-700">
        <div className="flex items-center gap-2">
          <Bell className="h-5 w-5 text-neutral-600 dark:text-neutral-400" />
          <h2 className="font-semibold text-neutral-900 dark:text-white">{t('notifications.title')}</h2>
          {unreadNotifications.length > 0 && (
            <span className="rounded-full bg-primary-100 px-2 py-0.5 text-xs font-medium text-primary-700 dark:bg-primary-900/30 dark:text-primary-400">
              {t('notifications.newCount', { count: unreadNotifications.length })}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {unreadNotifications.length > 0 && (
            <button
              onClick={handleMarkAllRead}
              className="rounded p-1.5 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-600 dark:hover:bg-neutral-800 dark:hover:text-neutral-300"
              title={t('notifications.markAllRead')}
            >
              <CheckCheck className="h-4 w-4" />
            </button>
          )}
          {notifications.length > 0 && (
            <button
              onClick={handleArchiveAll}
              className="rounded p-1.5 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-600 dark:hover:bg-neutral-800 dark:hover:text-neutral-300"
              title={t('notifications.archiveAll')}
            >
              <Archive className="h-4 w-4" />
            </button>
          )}
          <button
            onClick={onClose}
            className="rounded p-1.5 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-600 dark:hover:bg-neutral-800 dark:hover:text-neutral-300"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="max-h-[400px] overflow-y-auto">
        {notificationsLoading && (
          <div className="flex items-center justify-center py-8">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary-600 border-t-transparent" />
          </div>
        )}

        {notificationsError && (
          <div className="p-4 text-center text-sm text-red-600 dark:text-red-400">
            {t('notifications.loadError')}
          </div>
        )}

        {!notificationsLoading && !notificationsError && notifications.length === 0 && (
          <div className="py-12 text-center">
            <Bell className="mx-auto h-12 w-12 text-neutral-300 dark:text-neutral-600" />
            <p className="mt-2 text-sm text-neutral-500">{t('notifications.noNotifications')}</p>
          </div>
        )}

        {!notificationsLoading && !notificationsError && notifications.length > 0 && (
          <>
            {/* Unread section */}
            {unreadNotifications.length > 0 && (
              <div>
                <div className="sticky top-0 bg-neutral-50 px-4 py-2 text-xs font-medium uppercase tracking-wider text-neutral-500 dark:bg-neutral-800/50">
                  {t('notifications.sectionNew')}
                </div>
                {unreadNotifications.map((notification) => (
                  <NotificationItem
                    key={notification.id}
                    notification={notification}
                    onClick={() => handleNotificationClick(notification)}
                    onMarkRead={() => markAsRead([notification.id])}
                    onArchive={() => archiveNotification(notification.id)}
                  />
                ))}
              </div>
            )}

            {/* Read section */}
            {readNotifications.length > 0 && (
              <div>
                {unreadNotifications.length > 0 && (
                  <div className="sticky top-0 bg-neutral-50 px-4 py-2 text-xs font-medium uppercase tracking-wider text-neutral-500 dark:bg-neutral-800/50">
                    {t('notifications.sectionEarlier')}
                  </div>
                )}
                {readNotifications.map((notification) => (
                  <NotificationItem
                    key={notification.id}
                    notification={notification}
                    onClick={() => handleNotificationClick(notification)}
                    onArchive={() => archiveNotification(notification.id)}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Footer */}
      {notifications.length > 0 && (
        <div className="border-t border-neutral-200 p-2 dark:border-neutral-700">
          <button
            onClick={() => {
              navigate('/notifications');
              onClose();
            }}
            className="w-full rounded-lg py-2 text-center text-sm font-medium text-primary-600 hover:bg-primary-50 dark:text-primary-400 dark:hover:bg-primary-900/20"
          >
            {t('notifications.viewAll')}
          </button>
        </div>
      )}
    </div>
  );
}

interface NotificationItemProps {
  notification: Notification;
  onClick: () => void;
  onMarkRead?: () => void;
  onArchive: () => void;
}

function NotificationItem({
  notification,
  onClick,
  onMarkRead,
  onArchive,
}: NotificationItemProps) {
  const { t } = useTranslation();
  const Icon = getNotificationIcon(notification.notificationType);
  const priorityColor = getPriorityColor(notification.priority);

  return (
    <div
      className={clsx(
        'group relative flex items-start gap-3 px-4 py-3 transition-colors',
        'hover:bg-neutral-50 dark:hover:bg-neutral-800/50',
        !notification.isRead && 'bg-primary-50/50 dark:bg-primary-900/10'
      )}
    >
      {/* Unread indicator */}
      {!notification.isRead && (
        <div className="absolute left-1 top-1/2 h-2 w-2 -translate-y-1/2 rounded-full bg-primary-500" />
      )}

      {/* Icon or avatar */}
      <div className="flex-shrink-0">
        {notification.actor ? (
          <Avatar
            {...(notification.actor.avatarUrl ? { src: notification.actor.avatarUrl } : {})}
            name={notification.actor.displayName}
            size="sm"
          />
        ) : (
          <div
            className={clsx(
              'flex h-8 w-8 items-center justify-center rounded-full',
              'bg-neutral-100 dark:bg-neutral-800',
              priorityColor
            )}
          >
            <Icon className="h-4 w-4" />
          </div>
        )}
      </div>

      {/* Content */}
      <button onClick={onClick} className="min-w-0 flex-1 text-left">
        <div className="text-sm font-medium text-neutral-900 dark:text-white">
          {notification.title}
        </div>
        <div className="mt-0.5 line-clamp-2 text-sm text-neutral-600 dark:text-neutral-400">
          {notification.message}
        </div>
        <div className="mt-1 text-xs text-neutral-500">
          {formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true })}
        </div>
      </button>

      {/* Actions */}
      <div className="flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
        {onMarkRead && !notification.isRead && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onMarkRead();
            }}
            className="rounded p-1 text-neutral-400 hover:bg-neutral-200 hover:text-neutral-600 dark:hover:bg-neutral-700 dark:hover:text-neutral-300"
            title={t('notifications.markAsRead')}
          >
            <CheckCircle className="h-4 w-4" />
          </button>
        )}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onArchive();
          }}
          className="rounded p-1 text-neutral-400 hover:bg-neutral-200 hover:text-neutral-600 dark:hover:bg-neutral-700 dark:hover:text-neutral-300"
          title={t('notifications.archive')}
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
